import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { Menu, Tile, Split, Text } from './Nav.Desktop.Foldout.style';
import { Icon } from 'assets/Icons_';
import { Link } from 'react-router-dom';
import { MENU } from 'constants';
import { useDetectClickOutside } from 'react-detect-click-outside';

import { _project } from "state/store.projects";
import { active_project, current_language, kioskMode, params_query } from "state/store.global"; 
import { getFocus, useAtom, useGet } from "state/jotai";
import { PoweredBy } from 'components/PoweredBy/PoweredBy';
import { getProjectData } from 'state/jotai.projectDataHooks';
import { useWindowSize } from 'hooks/useWindowResize';
import { useSendClick } from 'hooks/fetch/useSendClick';

export const Foldout = ({ nav, show, setShow }) => {
  const KioskMode = useGet(kioskMode);
  const params       = useGet(params_query);
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const [menuHeight, setMenuHeight]       = useState(null);
  const sendClick = useSendClick();
  const window       = useWindowSize();
  const { pin, pid } = useGet(active_project);
  const menus        = getFocus(_project, "menuItems")?.[pin]?.[pid] ?? [];
  const downloads    = getProjectData('downloads', 0, []);
 
  const language = useAtom(current_language)[0];
  const buttonsLabel           = getFocus(_project, 'buttonsLabel')?.[pin]?.[pid] ?? [];
  const menuRef = useRef();
  
  const headerLabel = useMemo(() => {
    return buttonsLabel?.filter(
      (btn) => btn?.UILabelProperty === "Hvorvildugernehen"
    )[0]?.Label;
  }, [buttonsLabel]);
  
  const menu = {
    initial   : { x: +MENU.DESKTOP_NAV_DRAWER_W + 'px' },
    animate   : { x: 300 + 'px' },
    exit      : { x: +MENU.DESKTOP_NAV_DRAWER_W + 'px' },
    transition: { duration: 0.3, ease: 'linear' },
  };
  
  const animation = {
    initial   : { opacity: 0 },
    animate   : { opacity: 1 },
    exit      : { opacity: 0 },
    transition: { duration: 0.5, ease: "linear" },
  };

  const dynamicFilter = useCallback((item, downloadsLength) => {
    // Exclude items with Order "10" regardless of downloads length
    if (item.Order === "10") return false;
    
    // Exclude items with Order "9" if downloads length is 0
    if (downloadsLength === 0 && item.Order === "9") return false;
    
    // Include all other items
    return true;
  }, []);

  const menusItemsFiltered = useMemo(() => {
    const uniqueItems = new Set();
    return menus
      .filter((item) => dynamicFilter(item, downloads.length))
      .filter((item) => {
        const isDuplicate = uniqueItems.has(item.Text);
        uniqueItems.add(item.Text);
        return !isDuplicate;
      })
      .map((items) => {
        // More efficient key conversion using Object.entries
        return Object.entries(items).reduce((acc, [key, value]) => {
          acc[key.toLowerCase()] = value;
          return acc;
        }, {});
      });
  }, [menus, downloads.length, dynamicFilter]);

  useEffect(() => {
    const bottomPadding   = 96;
    const footerBarHeight = 72;
    const height          = menuHeight || menuRef?.current?.clientHeight;

    if (menuRef.current && ((height + bottomPadding + footerBarHeight) > window.height)) {
      !menuHeight && setMenuHeight(menuRef.current.clientHeight);
      isMenuVisible && setIsMenuVisible(false);
    } else if (menuHeight < window.height) {
      !isMenuVisible && setIsMenuVisible(true);
    }
  }, [window, menuHeight, isMenuVisible]);
  
  return (
    <AnimatePresence>
      { show && 
        <Menu 
          as={motion.div}
          clr={clr} 
          {...animation}
          ref={menuRef}
        >
          <div 
            style={{
              color: clr?.primary_text,
              padding: "16px", 
              borderBottom: `1px solid ${clr.primary_text}`, 
              display: 'flex', 
              justifyContent: "space-between", 
              alignItems: "center"
            }}
          >
            <div className='h5' style={{width:"100%", display:"flex", justifyContent:"center"}}>
              {headerLabel}
            </div>
            <span className='icon-hover-effect '>
            <Icon 
              icon='close'
              isFaIcon={true} 
              click={() => setShow(false)} 
              size="xl" 
              color={clr?.primary_text}
            />
            </span>
          </div>

          <div style={{
            padding: "32px 24px 0px 24px", 
            height: isMenuVisible ? "auto" : "calc(100vh - 255px)", 
            overflow: 'auto'
          }}>
          { 
            menusItemsFiltered.map(({ order, url, text, icon }, j) => {
              if (KioskMode && order < 8) {
                return null; // Skip rendering this item
              }
              const ico = {href: url[language], icon, size: "16", color: clr?.primary_text};
              const URL = (url.includes('gallery') || url.includes('downloads')) && params.length ? url + params : url;
              return ( 
                <div 
                  key={text} 
                  onClick={() => {
                     sendClick("menu_button", { menuItem: text });
                    setShow(false)} } 
                >
                  <Link 
                    clr={clr} 
                    to={URL} 
                    key={text} 
                    style={{textDecoration:'none'}}
                  >
                    <Tile index={j} clr={clr}>
                    <span className='icon-hover-effect' style={{display:"flex",gap:"8px"}}>
                      <Icon 
                      style={{fontSize:"16px"}}
                        {...ico} 
                        isFaIcon={false} 
                        FaIconColor={clr.primary_text}
                      />
                      <Text className='paragraph1-regular' clr={clr}>{text}</Text>
                      </span>
                    </Tile>
                  </Link>
                  <Split clr={clr}/>
                </div>
              );
            })
          }
          </div>
          <PoweredBy clr={clr.primary_text} padding='8px 0 16px 0px' />
        </Menu>
      }
    </AnimatePresence>
  );
};
